import type { ScheduleSlot } from "../../api/types.js";

/**
 * A week, as the brain reads it.
 *
 * The rules below are not this screen's invention: they are `findActiveSlot` in
 * `packages/domain/src/index.ts`, read and mirrored. A slot is anchored to the day it
 * *starts* on; an end at or before the start runs past midnight into the next day;
 * where several slots cover the same instant the highest priority governs, and equal
 * priorities are settled by id. A grid that laid these out any other way would draw a
 * week the product does not play.
 */

export const SECONDS_PER_DAY = 86_400;

/** 0 is Sunday, which is what `days_of_week` stores and what the domain compares. */
export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const DAY_ABBREVIATIONS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

export function timeToSeconds(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? "0");
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return hours * 3_600 + minutes * 60 + seconds;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** `HH:MM:SS`, which is the shape the API's `time` columns take. */
export function secondsToTime(seconds: number): string {
  const clamped = Math.max(
    0,
    Math.min(SECONDS_PER_DAY - 1, Math.round(seconds)),
  );
  return `${pad(Math.floor(clamped / 3_600))}:${pad(
    Math.floor((clamped % 3_600) / 60),
  )}:${pad(clamped % 60)}`;
}

/** `HH:MM`, which is what a person reads off a grid. */
export function formatClock(seconds: number): string {
  const whole =
    ((Math.round(seconds) % SECONDS_PER_DAY) + SECONDS_PER_DAY) %
    SECONDS_PER_DAY;
  return `${pad(Math.floor(whole / 3_600))}:${pad(
    Math.floor((whole % 3_600) / 60),
  )}`;
}

/** The clock face a `time` column shows, without its seconds when it has none. */
export function trimTime(value: string): string {
  const seconds = timeToSeconds(value);
  return seconds === null ? value : formatClock(seconds);
}

/**
 * One rectangle on the grid. A slot that runs past midnight is two of these — one
 * ending at midnight and one starting there — because that is what the week looks
 * like, and each says that it is the continuation of the other.
 */
export interface GridBlock {
  key: string;
  slotId: string;
  playlistId: string;
  priority: number;
  enabled: boolean;
  day: number;
  fromSeconds: number;
  toSeconds: number;
  continuesFromPreviousDay: boolean;
  continuesIntoNextDay: boolean;
  /** Which column of its overlapping cluster this block draws in. */
  lane: number;
  /** How many columns that cluster needs. */
  laneCount: number;
}

function blocksForSlot(
  slot: ScheduleSlot,
): Omit<GridBlock, "lane" | "laneCount">[] {
  const starts = timeToSeconds(slot.startsAt);
  const ends = timeToSeconds(slot.endsAt);
  if (starts === null || ends === null) return [];
  const blocks: Omit<GridBlock, "lane" | "laneCount">[] = [];
  for (const day of [...new Set(slot.daysOfWeek)].sort((a, b) => a - b)) {
    if (day < 0 || day > 6) continue;
    const wraps = ends <= starts;
    blocks.push({
      key: `${slot.id}:${String(day)}:a`,
      slotId: slot.id,
      playlistId: slot.playlistId,
      priority: slot.priority,
      enabled: slot.enabled,
      day,
      fromSeconds: starts,
      toSeconds: wraps ? SECONDS_PER_DAY : ends,
      continuesFromPreviousDay: false,
      continuesIntoNextDay: wraps,
    });
    if (wraps && ends > 0) {
      blocks.push({
        key: `${slot.id}:${String(day)}:b`,
        slotId: slot.id,
        playlistId: slot.playlistId,
        priority: slot.priority,
        enabled: slot.enabled,
        day: (day + 1) % 7,
        fromSeconds: 0,
        toSeconds: ends,
        continuesFromPreviousDay: true,
        continuesIntoNextDay: false,
      });
    }
  }
  return blocks;
}

/**
 * Every block of the week, each told which column to draw in.
 *
 * Overlaps are laid side by side rather than stacked, because two slots covering the
 * same hour is not an error — the higher priority governs and the other is what the
 * feed falls back to when the first has nothing to play. Hiding one would hide a
 * decision the operator made.
 */
export function layoutWeek(slots: readonly ScheduleSlot[]): GridBlock[] {
  const laid: GridBlock[] = [];
  for (let day = 0; day < 7; day += 1) {
    const ofDay = slots
      .flatMap(blocksForSlot)
      .filter((block) => block.day === day)
      .sort(
        (left, right) =>
          left.fromSeconds - right.fromSeconds ||
          right.priority - left.priority ||
          left.slotId.localeCompare(right.slotId),
      );
    const laneEnds: number[] = [];
    const assigned = ofDay.map((block) => {
      let lane = laneEnds.findIndex((end) => end <= block.fromSeconds);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(block.toSeconds);
      } else {
        laneEnds[lane] = block.toSeconds;
      }
      return { ...block, lane, laneCount: 1 };
    });
    const laneCount = Math.max(1, laneEnds.length);
    for (const block of assigned) laid.push({ ...block, laneCount });
  }
  return laid;
}

/**
 * Which slot actually governs one moment of the week — the same answer the brain
 * reaches, by the same rule: highest priority, and where priorities tie, the lowest
 * id. Disabled slots govern nothing.
 */
export function governingSlotAt(
  slots: readonly ScheduleSlot[],
  day: number,
  seconds: number,
): ScheduleSlot | null {
  const covering = slots.filter((slot) => {
    if (!slot.enabled) return false;
    return blocksForSlot(slot).some(
      (block) =>
        block.day === day &&
        seconds >= block.fromSeconds &&
        seconds < block.toSeconds,
    );
  });
  return (
    [...covering].sort(
      (left, right) =>
        right.priority - left.priority || left.id.localeCompare(right.id),
    )[0] ?? null
  );
}

/** "Every day", "Mon–Fri", "Sat, Sun" — a run of days said the way a person says it. */
export function formatDays(daysOfWeek: readonly number[]): string {
  const days = [...new Set(daysOfWeek)]
    .filter((day) => day >= 0 && day <= 6)
    .sort((left, right) => left - right);
  if (days.length === 0) return "No day";
  if (days.length === 7) return "Every day";
  const runs: number[][] = [];
  for (const day of days) {
    const last = runs.at(-1);
    if (last && day === (last.at(-1) ?? -2) + 1) last.push(day);
    else runs.push([day]);
  }
  return runs
    .map((run) => {
      const first = DAY_ABBREVIATIONS[run[0] ?? 0] ?? "";
      if (run.length === 1) return first;
      const last = DAY_ABBREVIATIONS[run.at(-1) ?? 0] ?? "";
      return run.length === 2 ? `${first}, ${last}` : `${first}–${last}`;
    })
    .join(", ");
}

/** One slot said in a line: when it runs, and whether it runs past midnight. */
export function describeSlot(slot: ScheduleSlot): string {
  const starts = timeToSeconds(slot.startsAt);
  const ends = timeToSeconds(slot.endsAt);
  if (starts === null || ends === null) return formatDays(slot.daysOfWeek);
  const overnight = ends <= starts;
  return `${formatDays(slot.daysOfWeek)} · ${formatClock(starts)}–${formatClock(
    ends,
  )}${overnight ? " (overnight)" : ""}`;
}
